import { City, TripLeg } from "../data/types";
import { getPOICached } from "../services/poiService";
import { ACTION_COLORS, countryFlagImg } from "../data/design-tokens";
import type { StopActions } from "../data/types";

interface TripPdfOptions {
  tripName: string;
  travelDate: string;
  stops: City[];
  stopActions?: StopActions;
  tripLegs?: TripLeg[];
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getCountriesVisited(stops: City[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const s of stops) {
    if (!seen.has(s.country)) {
      seen.add(s.country);
      ordered.push(s.country);
    }
  }
  return ordered;
}

function renderStopCard(
  stop: City,
  physicalStationName: string,
  index: number,
  total: number,
  actions?: Partial<StopActions[string]>
): string {
  const flag = countryFlagImg(stop.country, 18);
  const isFirst = index === 0;
  const isLast = index === total - 1;

  // Check cached POI status (pre-warmed before PDF generation)
  const poiCheck = getPOICached(stop.city);
  const badges: string[] = [];
  if (isFirst) badges.push('<span class="badge badge-depart">DEPARTURE</span>');
  if (isLast) badges.push('<span class="badge badge-arrive">ARRIVAL</span>');
  if (actions?.overnight) badges.push('<span class="badge badge-overnight">OVERNIGHT</span>');
  if (actions?.food) badges.push('<span class="badge badge-food">FOOD STOP</span>');
  if (actions?.fika) badges.push(`<span class="badge badge-food" style="background:${escapeHtml(ACTION_COLORS.fika)};color:white">FIKA</span>`);
  if (poiCheck?.poi_type === "heritage-landmark") badges.push(`<span class="badge badge-heritage">HERITAGE</span>`);
  else if (poiCheck?.poi_type === "hidden-gem") badges.push(`<span class="badge badge-gem">HIDDEN GEM</span>`);

  const attractionsHtml =
    stop.attractions && stop.attractions.length > 0
      ? `<div class="stop-section">
          <div class="section-label">SEE</div>
          <div class="section-items">${stop.attractions.map((a) => `<span class="item">${escapeHtml(a)}</span>`).join("")}</div>
        </div>`
      : "";

  const foodHtml =
    stop.foodSpecialties && stop.foodSpecialties.length > 0
      ? `<div class="stop-section">
          <div class="section-label">EAT</div>
          <div class="section-items">${stop.foodSpecialties.map((f) => `<span class="item">${escapeHtml(f)}</span>`).join("")}</div>
        </div>`
      : "";

  return `
    <div class="stop-card">
      <div class="timeline-col">
        <div class="stop-number">${index + 1}</div>
        ${index < total - 1 ? '<div class="timeline-line"></div>' : ""}
      </div>
      <div class="stop-content">
        <div class="stop-header">
          <div class="stop-title">
            <span class="stop-flag">${flag}</span>
            <span class="stop-city">${escapeHtml(stop.city)}</span>
            <span class="stop-country">${escapeHtml(stop.country)}</span>
          </div>
          ${badges.length > 0 ? `<div class="badges">${badges.join("")}</div>` : ""}
        </div>
        <div class="stop-station">${escapeHtml(physicalStationName)}</div>
        ${attractionsHtml}
        ${foodHtml}
      </div>
    </div>
  `;
}

export function generateTripPdf(options: TripPdfOptions): void {
  const { tripName, travelDate, stops, stopActions = {}, tripLegs = [] } = options;

  if (stops.length === 0) return;

  const countries = getCountriesVisited(stops);
  const overnightCount = stops.reduce((sum, stop) => sum + (stopActions[stop.id]?.overnight ? stopActions[stop.id].nights || 1 : 0), 0);

  // Build leg lookup for journey connectors between stops
  const legMap = new Map<string, TripLeg>();
  for (const leg of tripLegs) {
    legMap.set(`${leg.fromStationId}→${leg.toStationId}`, leg);
  }

  const stopsHtml = stops
    .map((stop, i) => {
      const prevLeg = i > 0 ? legMap.get(`${stops[i - 1].id}→${stop.id}`) : undefined;
      const nextLeg = i < stops.length - 1 ? legMap.get(`${stop.id}→${stops[i + 1].id}`) : undefined;

      let physicalStationName = stop.name;
      const arrivalName = prevLeg?.stationData?.at(-1)?.name;
      const departureName = nextLeg?.stationData?.[0]?.name;

      if (arrivalName && departureName && arrivalName !== departureName) {
        physicalStationName = `Arr: ${arrivalName} / Dep: ${departureName}`;
      } else if (arrivalName) {
        physicalStationName = arrivalName;
      } else if (departureName) {
        physicalStationName = departureName;
      }

      let html = renderStopCard(stop, physicalStationName, i, stops.length, stopActions[stop.id]);
      // Insert journey connector between consecutive stops
      if (i < stops.length - 1) {
        const nextStop = stops[i + 1];
        const leg = legMap.get(`${stop.id}→${nextStop.id}`);
        if (leg) {
          html += `
            <div class="journey-connector">
              <span class="journey-arrow">▼</span>
              <span class="journey-times">${escapeHtml(leg.departureTime)} → ${escapeHtml(leg.arrivalTime)}</span>
              <span class="journey-operator">${escapeHtml(leg.operator)}</span>
            </div>
          `;
        }
      }
      return html;
    })
    .join("");

  const countriesLine = countries
    .map((c) => `${countryFlagImg(c, 16)} ${escapeHtml(c)}`)
    .join("&nbsp;&nbsp;&middot;&nbsp;&nbsp;");

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(tripName)} — Via Fera Itinerary</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      background: #f8f8fa;
      color: #1a1a2e;
      line-height: 1.5;
      -webkit-font-smoothing: antialiased;
    }

    .page {
      max-width: 680px;
      margin: 0 auto;
      padding: 48px 40px 64px;
      background: white;
      min-height: 100vh;
    }

    /* ─── Header ───────────────────────────── */
    .header {
      text-align: center;
      margin-bottom: 48px;
      padding-bottom: 32px;
      border-bottom: 1px solid #e8e8ed;
    }

    .header-logo {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 2.5px;
      color: #6366f1;
      margin-bottom: 20px;
    }

    .header-logo svg {
      width: 16px;
      height: 16px;
    }

    .trip-name {
      font-size: 32px;
      font-weight: 700;
      color: #1a1a2e;
      margin-bottom: 8px;
      letter-spacing: -0.5px;
    }

    .trip-date {
      font-size: 15px;
      color: #6b7280;
      font-weight: 400;
    }

    /* ─── Stats Bar ────────────────────────── */
    .stats {
      display: flex;
      justify-content: center;
      gap: 32px;
      margin-bottom: 12px;
      padding: 20px 0;
      border-bottom: 1px solid #f0f0f5;
    }

    .stat {
      text-align: center;
    }

    .stat-value {
      font-size: 24px;
      font-weight: 700;
      color: #1a1a2e;
    }

    .stat-label {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #9ca3af;
      margin-top: 2px;
    }

    /* ─── Countries ────────────────────────── */
    .countries {
      text-align: center;
      font-size: 13px;
      color: #6b7280;
      margin-bottom: 40px;
      padding-bottom: 32px;
      border-bottom: 1px solid #f0f0f5;
    }

    /* ─── Journey Section ──────────────────── */
    .section-title {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 2px;
      color: #9ca3af;
      margin-bottom: 24px;
    }

    /* ─── Stop Cards ───────────────────────── */
    .stop-card {
      display: flex;
      gap: 20px;
      margin-bottom: 0;
    }

    .timeline-col {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 36px;
      flex-shrink: 0;
    }

    .stop-number {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: #6366f1;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 700;
      flex-shrink: 0;
    }

    .timeline-line {
      width: 2px;
      flex: 1;
      min-height: 20px;
      background: linear-gradient(to bottom, #6366f1, #a5b4fc);
    }

    .stop-content {
      flex: 1;
      padding-bottom: 28px;
      min-width: 0;
    }

    .stop-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 4px;
    }

    .stop-title {
      display: flex;
      align-items: baseline;
      gap: 8px;
    }

    .stop-flag {
      display: inline-flex;
      align-items: center;
      line-height: 1;
    }

    .stop-flag img {
      width: 18px;
      height: 18px;
    }

    .stop-city {
      font-size: 19px;
      font-weight: 700;
      color: #1a1a2e;
      letter-spacing: -0.3px;
    }

    .stop-country {
      font-size: 13px;
      color: #9ca3af;
      font-weight: 400;
    }

    .stop-station {
      font-size: 12px;
      color: #9ca3af;
      margin-bottom: 12px;
      font-style: italic;
    }

    /* ─── Badges ───────────────────────────── */
    .badges {
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      flex-shrink: 0;
    }

    .badge {
      font-size: 8px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
      padding: 3px 8px;
      border-radius: 4px;
      white-space: nowrap;
    }

    .badge-depart { background: #eff6ff; color: #3b82f6; }
    .badge-arrive { background: #fef2f2; color: #ef4444; }
    .badge-overnight { background: #f5f3ff; color: #7c3aed; }
    .badge-food { background: #fef2f2; color: #ef4444; }
    .badge-gem { background: #e0f2fe; color: #0369a1; }
    .badge-heritage { background: #eef2ff; color: #4f46e5; }

    /* ─── Stop sections ────────────────────── */
    .stop-section {
      margin-bottom: 8px;
    }

    .section-label {
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #6366f1;
      margin-bottom: 4px;
    }

    .section-items {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .item {
      font-size: 12px;
      color: #4b5563;
      background: #f9fafb;
      border: 1px solid #f0f0f5;
      padding: 3px 10px;
      border-radius: 6px;
    }

    /* ─── Journey Connector ────────────────── */
    .journey-connector {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 0 8px 56px;
      font-size: 11px;
      color: #6b7280;
    }

    .journey-connector .journey-times {
      font-weight: 600;
      color: #4b5563;
    }

    .journey-connector .journey-operator {
      font-size: 10px;
      color: #9ca3af;
    }

    .journey-connector .journey-arrow {
      color: #6366f1;
      font-size: 10px;
    }

    /* ─── Footer ───────────────────────────── */
    .footer {
      margin-top: 48px;
      padding-top: 24px;
      border-top: 1px solid #e8e8ed;
      text-align: center;
    }

    .footer-text {
      font-size: 11px;
      color: #9ca3af;
    }

    .footer-brand {
      font-size: 12px;
      font-weight: 600;
      color: #6366f1;
      margin-bottom: 4px;
    }

    .footer-tagline {
      font-size: 10px;
      color: #c4c4cc;
      font-style: italic;
    }

    /* ─── Print Controls (hidden when printing) */
    .print-controls {
      position: fixed;
      bottom: 24px;
      right: 24px;
      display: flex;
      gap: 8px;
      z-index: 100;
    }

    .print-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 12px;
      border: none;
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s ease;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }

    .print-btn-primary {
      background: #6366f1;
      color: white;
    }

    .print-btn-primary:hover {
      background: #4f46e5;
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
    }

    .print-btn-secondary {
      background: white;
      color: #4b5563;
      border: 1px solid #e5e7eb;
    }

    .print-btn-secondary:hover {
      background: #f9fafb;
      transform: translateY(-1px);
    }

    .print-hint {
      position: fixed;
      bottom: 76px;
      right: 24px;
      font-family: 'Inter', sans-serif;
      font-size: 11px;
      color: #9ca3af;
      z-index: 100;
    }

    /* ─── Print styles ─────────────────────── */
    @media print {
      body { background: white; }

      .page {
        padding: 32px 24px 40px;
        max-width: none;
      }

      .print-controls, .print-hint { display: none !important; }

      .stop-card { break-inside: avoid; }

      .header { break-after: avoid; }

      .stop-number { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .badge { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .timeline-line { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .item { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }

    @page {
      margin: 0.5in;
      size: A4;
    }
  </style>
</head>
<body>
  <div class="page">
    <!-- Header -->
    <div class="header">
      <div class="header-logo">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M2 12h20"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        VIA FERA ITINERARY
      </div>
      <div class="trip-name">${escapeHtml(tripName)}</div>
      <div class="trip-date">${formatDate(travelDate)}</div>
    </div>

    <!-- Stats -->
    <div class="stats">
      <div class="stat">
        <div class="stat-value">${stops.length}</div>
        <div class="stat-label">Stops</div>
      </div>
      <div class="stat">
        <div class="stat-value">${countries.length}</div>
        <div class="stat-label">Countries</div>
      </div>
      ${overnightCount > 0
      ? `<div class="stat">
              <div class="stat-value">${overnightCount}</div>
              <div class="stat-label">Overnights</div>
            </div>`
      : ""
    }
    </div>

    <!-- Countries -->
    <div class="countries">${countriesLine}</div>

    <!-- Journey -->
    <div class="section-title">Your Journey</div>
    ${stopsHtml}

    <!-- Footer -->
    <div class="footer">
      <div class="footer-brand">Via Fera</div>
      <div class="footer-text">See Europe at the pace it was meant to be seen.</div>
      <div class="footer-tagline">Generated on ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
    </div>
  </div>

  <!-- Print Controls -->
  <div class="print-controls">
    <button class="print-btn print-btn-secondary">Close</button>
    <button class="print-btn print-btn-primary">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
      Save as PDF
    </button>
  </div>
  <div class="print-hint">Tip: In the print dialog, select "Save as PDF" as destination</div>
</body>
</html>`;

  const documentUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const newWindow = globalThis.open(documentUrl, "_blank");
  if (newWindow) newWindow.addEventListener("load", () => {
    newWindow.document.querySelector(".print-btn-primary")?.addEventListener("click", () => newWindow.print());
    newWindow.document.querySelector(".print-btn-secondary")?.addEventListener("click", () => newWindow.close());
    URL.revokeObjectURL(documentUrl);
  }, { once: true });
  else URL.revokeObjectURL(documentUrl);
}
